<template>
  <div class="church-schedule">
    <h1 class="title">Church Schedule {{ scheduleYear }}</h1>
    
    <div class="notes">
      <p>Note: If you are scheduled for an activity and you can't make it, it is your responsibility to find a replacement.</p>
    </div>

    <!-- Navigation controls -->
    <div class="navigation">
      <button @click="previousWeek" class="nav-button">
        <span class="arrow">←</span> Previous
      </button>
      <button @click="goToCurrentWeek" class="current-button">
        Current Week
      </button>
      <button @click="nextWeek" class="nav-button">
        Next <span class="arrow">→</span>
      </button>
    </div>

    <!-- Card View -->
    <div class="schedule-card">
      <div class="date-header">
        {{ formatDate(currentWeekData[0]) }}
      </div>
      <div class="card-content">
        <div class="service-detail">
          <label>Preacher:</label>
          <span>{{ currentWeekData[1] }}</span>
        </div>
        <div class="service-detail">
          <label>Worship Leader:</label>
          <span>{{ currentWeekData[2] }}</span>
        </div>
        <div class="service-detail" v-if="currentWeekData[3] !== '-'">
          <label>Communion:</label>
          <span>{{ currentWeekData[3] }}</span>
        </div>
        <div class="service-detail" v-if="currentWeekData[4] || currentWeekData[3] !== '-'">
          <label>Special Notes:</label>
          <span>{{ formatNotes(currentWeekData[4] + ' ' + currentWeekData[3]) }}</span>
        </div>
        <div class="share-section">
          <button @click="toggleShareOptions" class="share-button">
            <span>Share</span>
          </button>
          
          <!-- Show options inline instead of absolutely positioned -->
          <div v-if="showShareOptions" class="share-options-simple">
            <a :href="whatsappShareUrl" target="_blank" class="share-option-simple whatsapp">
              Share via WhatsApp
            </a>
            <a :href="emailShareUrl" class="share-option-simple email">
              Share via Email
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- View toggle -->
    <div class="view-toggle">
      <button @click="showFullSchedule = !showFullSchedule" class="toggle-button">
        {{ showFullSchedule ? 'Show Card View' : 'Show Full Schedule' }}
      </button>
    </div>

    <!-- Full Schedule Table (conditionally rendered) -->
    <div v-if="showFullSchedule" class="schedule-table-wrapper">
      <table class="schedule-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Preacher (Sermon)</th>
            <th>Worship Leader</th>
            <th>Communion?</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in scheduleData" :key="index">
            <td>{{ row[0] }}</td>
            <td>{{ row[1] }}</td>
            <td>{{ row[2] }}</td>
            <td>{{ row[3] }}</td>
            <td>{{ formatNotes(row[4] + ' ' + row[3]) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import scheduleJson from '../../public/schedule.json';

const currentIndex = ref(0);
const showFullSchedule = ref(false);
const showShareOptions = ref(false);

// Debug logs
console.log('Component initialized');
console.log('Schedule data loaded:', scheduleJson.scheduleData.length, 'items');

// Get schedule data and year from the JSON file
const scheduleData = scheduleJson.scheduleData;
const scheduleYear = scheduleJson.year || new Date().getFullYear();

// Current week's data
const currentWeekData = computed(() => {
  const data = scheduleData[currentIndex.value] || scheduleData[0];
  console.log('Current week data:', data);
  return data;
});

// Toggle share options display - add debug logs
const toggleShareOptions = () => {
  console.log('Toggle share button clicked. Current state:', showShareOptions.value);
  showShareOptions.value = !showShareOptions.value;
  console.log('New share options state:', showShareOptions.value);
  
  // Close the share options when clicking outside
  if (showShareOptions.value) {
    console.log('Setting up click-outside listener');
    setTimeout(() => {
      const closeOnClickOutside = (e) => {
        const shareSection = document.querySelector('.share-section');
        console.log('Click detected, checking if outside:', shareSection, e.target);
        if (shareSection && !shareSection.contains(e.target)) {
          console.log('Click was outside, closing menu');
          showShareOptions.value = false;
          document.removeEventListener('click', closeOnClickOutside);
        }
      };
      document.addEventListener('click', closeOnClickOutside);
    }, 100);
  }
};

// Generate share text for current schedule
const shareText = computed(() => {
  const date = formatDate(currentWeekData.value[0]);
  const preacher = currentWeekData.value[1];
  const worshipLeader = currentWeekData.value[2];
  
  let text = `GBC Schedule for ${date}:\n`;
  text += `Preacher: ${preacher}\n`;
  text += `Worship Leader: ${worshipLeader}\n`;
  
  if (currentWeekData.value[3] !== '-') {
    text += `Communion: ${currentWeekData.value[3]}\n`;
  }
  
  const notes = formatNotes(currentWeekData.value[4] + ' ' + currentWeekData.value[3]);
  if (notes) {
    text += `Notes: ${notes}`;
  }
  
  console.log('Generated share text:', text);
  return text;
});

// Share URLs with debug logs
const whatsappShareUrl = computed(() => {
  const url = `https://wa.me/?text=${encodeURIComponent(shareText.value)}`;
  console.log('WhatsApp URL:', url);
  return url;
});

const emailShareUrl = computed(() => {
  const url = `mailto:?subject=GBC Schedule for ${encodeURIComponent(formatDate(currentWeekData.value[0]))}&body=${encodeURIComponent(shareText.value)}`;
  console.log('Email URL:', url);
  return url;
});

// Add watcher for share options visibility
watch(showShareOptions, (newValue) => {
  console.log('Share options visibility changed to:', newValue);
});

// Navigation methods
const nextWeek = () => {
  if (currentIndex.value < scheduleData.length - 1) {
    currentIndex.value++;
  }
};

const previousWeek = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
  }
};

const goToCurrentWeek = () => {
  currentIndex.value = findNextSundayIndex();
};

// Format date for display
const formatDate = (dateStr) => {
  const [month, dayPart] = dateStr.split(' ');
  const day = dayPart.split('(')[0];
  
  // Handle Good Friday differently
  if (dayPart.includes('Fri')) {
    return `Friday, ${month} ${day}, ${scheduleYear}`;
  }

  return `Sunday, ${month} ${day}, ${scheduleYear}`;
};

// Helper to format notes for display
const formatNotes = (notes) => {
  const noteArray = [];
  if (notes.includes('AGM')) noteArray.push('AGM after service');
  if (notes.includes('Prayer Meeting')) noteArray.push('Prayer Meeting');
  if (notes.includes('Communion')) noteArray.push('Communion');
  if (notes.includes('Good Friday')) noteArray.push('Good Friday');
  if (notes.includes('Easter')) noteArray.push('Easter Sunday');
  return noteArray.join(' • ');
};

// Find the index of the next Sunday
const findNextSundayIndex = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time for accurate date comparison

  // If we're before the schedule year, show first entry
  if (today.getFullYear() < scheduleYear) {
    return 0;
  }

  const monthMap = {
    "Jan": 0, "Feb": 1, "Mar": 2, "Apr": 3, "May": 4, "Jun": 5,
    "Jul": 6, "Aug": 7, "Sep": 8, "Oct": 9, "Nov": 10, "Dec": 11
  };

  // Find the next Sunday (or today if it's Sunday)
  const index = scheduleData.findIndex(row => {
    const [month, dayPart] = row[0].split(' ');
    const dayNum = parseInt(dayPart.split('(')[0]);
    const monthNum = monthMap[month];
    const scheduleDate = new Date(scheduleYear, monthNum, dayNum);
    scheduleDate.setHours(0, 0, 0, 0);

    // Show this entry if it's today or in the future
    return scheduleDate >= today;
  });

  // If past end of schedule, show last entry
  return index === -1 ? scheduleData.length - 1 : index;
};

onMounted(() => {
  console.log('Component mounted');
  goToCurrentWeek();
  
  // Add direct DOM check for share button
  setTimeout(() => {
    const shareButton = document.querySelector('.share-button');
    console.log('Share button found in DOM:', !!shareButton);
    if (shareButton) {
      console.log('Share button styles:', window.getComputedStyle(shareButton));
    }
    
    // Test direct click handler
    shareButton?.addEventListener('click', () => {
      console.log('Direct click handler on share button fired');
    });
  }, 1000);
});
</script>

<style scoped>
.church-schedule {
  padding: 16px;
  max-width: 800px;
  margin: 0 auto;
}

.title {
  text-align: center;
  font-size: clamp(18px, 4vw, 24px);
  margin-bottom: 16px;
}

.notes {
  text-align: center;
  margin-bottom: 20px;
  font-size: clamp(12px, 3vw, 14px);
}

.navigation {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
}

.nav-button, .current-button {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.nav-button {
  background-color: #f0f0f0;
}

.current-button {
  background-color: #e0e0e0;
}

.arrow {
  font-size: 16px;
}

.schedule-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
}

.date-header {
  background-color: #f5f5f5;
  padding: 16px;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}

.card-content {
  padding: 16px;
}

.service-detail {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.service-detail:last-child {
  border-bottom: none;
}

.service-detail label {
  font-weight: bold;
  color: #666;
}

.service-detail span {
  color: #2c3e50;
  text-align: right;
}

/* Add styles for special notes */
.service-detail span:has(+ .agm) {
  color: #e67e22;
  font-weight: 500;
}

.view-toggle {
  text-align: center;
  margin: 20px 0;
}

.toggle-button {
  padding: 8px 16px;
  background-color: #f0f0f0;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

/* Full schedule table styles */
.schedule-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px;
  margin-bottom: 20px;
}

.schedule-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.schedule-table th,
.schedule-table td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid #eee;
}

.schedule-table th {
  background-color: #f5f5f5;
  font-weight: bold;
  color: #666;
}

.schedule-table tr:last-child td {
  border-bottom: none;
}

.schedule-table tr:hover {
  background-color: #f9f9f9;
}

/* Media Queries */
@media screen and (max-width: 480px) {
  .church-schedule {
    padding: 12px;
  }
  
  .navigation {
    flex-wrap: wrap;
  }
  
  .nav-button, .current-button {
    flex: 1;
    min-width: 120px;
    padding: 12px;
  }
}

.share-section {
  margin-top: 16px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.share-button {
  background-color: #25D366;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 200px;
}

.share-options-simple {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 240px;
}

.share-option-simple {
  padding: 10px 16px;
  text-decoration: none;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
}

.share-option-simple.whatsapp {
  background-color: #25D366;
  color: white;
}

.share-option-simple.email {
  background-color: #4285F4;
  color: white;
}
</style> 